import { share, map, flatMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { DataService, ApiService, InspectionPluginService } from '../../shared/services/index';
import * as _ from 'lodash';
import { ApiMode } from '../../utils/enums';
import { OfflineApiService } from '../../shared/services/offlineApi.service';
import { AppConstants } from '../../utils/appConstants.class';
import { CustomerSelectorFactoryPluginService } from './customerselector-plugin-factory.service';
import { LogService } from '../../shared/services/log.service';

@Injectable()
export class CustomerSelectorPluginService extends InspectionPluginService {
  private countriesLoader: Observable<any>;
  private countriesByCode: any = {};

  constructor(
    protected apiService: ApiService,
    protected dataService: DataService,
    offlineApi: OfflineApiService,
    protected customerFactory: CustomerSelectorFactoryPluginService,
    protected logService: LogService
  ) {
    super(apiService, dataService, offlineApi, customerFactory);
    this.loadCountries();
  }

  public languages: any = [
    { label: 'English', value: 'en' },
    { label: 'Deutsch', value: 'de' },
    { label: 'Español', value: 'es' },
    { label: 'Français', value: 'fr' },
    { label: 'Italiano', value: 'it' },
    { label: 'Português', value: 'pt' },
    { label: 'Polski', value: 'pl' },
    { label: 'Czech', value: 'cs' },
    { label: '中文', value: 'zh' },
    { label: '日本語', value: 'jp' },
    { label: 'русский', value: 'ru' },
    { label: 'Türkçe', value: 'tr' }
  ];

  public updateCustomer(inspectionId: string, pluginId: string, customer: any): Observable<any> {
    return this._update(inspectionId, pluginId, customer).pipe(
      map((res: any) => {
        this.dataService.updateCustomer(inspectionId, res);
        return this.dataService.updatePluginConfig(inspectionId, pluginId, {
          ...res
        });
      })
    );
  }

  getLanguages() {
    return this.languages;
  }

  public getCountries(): Observable<any> {
    if (this.countriesLoader) {
      return this.countriesLoader;
    }
    return new BehaviorSubject(_.values(this.countriesByCode)).asObservable();
  }

  public loadCountries() {
    const self = this;
    this.countriesLoader = this.loadAllCountries().pipe(
      share(),
      map((res: any) => {
        this.countriesLoader = null;
        const countries = res;

        _.map(countries, (el) => {
          self.countriesByCode[el.Code] = { label: el.Name, value: el.Code };
        });

        return _.values(this.countriesByCode);
      })
    );
    this.countriesLoader.subscribe(
      (data: any) => {},
      (err: any) => this.logService.logError(err, 'Countries load error')
    );
  }

  private loadAllCountries() {
    const url = '/inspections/plugins/customer/countries/list';
    return this.offlineApi.getWithMode(url, ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.countries);
  }

  public getCountry(code: string): Observable<any> {
    return new BehaviorSubject(this.countriesByCode[code]).asObservable();
  }

  private _update(inspectionId: string, pluginId: string, customer: any): Observable<any> {
    customer.CrmId = customer.CrmId ?? 'nocrmId';
    return this.customerFactory.updateCustomer(inspectionId, pluginId, customer).pipe(
      flatMap((res) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(res.customer);
        }
        return this.apiService.post(res.plugin.postServerUrl, res.plugin.config);
      })
    );
  }
}
